// 서리 균열(R3)·독 늪(R4)·유사(R5)·심연 균열(R6) 웅덩이 비주얼(D3a, C2). 판정 칸(levels MapDef.lava)과 같은 사각형을 바닥 바로 위 판 하나(병합)로 덮는다 —
// 보이는 곳이 곧 위험한 곳이다. 셰이더는 월드 좌표 잡음 두 번 + 시간 흐름(점멸·섬광 없음, 밝기는 천천히 흐르기만).
// 드로우콜 1, 프레임 할당 없음(시간은 TSL time).
import * as THREE from 'three/webgpu';
import { abs, float, mix, mx_noise_float, positionWorld, smoothstep, time, uv, vec3 } from 'three/tsl';
import type { HazardTheme, Rect } from '../../shared/levels';

/** 판 높이(바닥 위, 그림자·바닥과 깜박이지 않게) */
const Y = 0.035;

export function buildHazardPools(rects: readonly Rect[], theme: HazardTheme): THREE.Mesh {
  const pos: number[] = [], uvs: number[] = [], idx: number[] = [];
  let base = 0;
  for (const r of rects) {
    // 가장자리를 0.15m 넓혀 칸 경계가 딱 끊겨 보이지 않게(판정은 칸 그대로)
    const x0 = r.x0 - 0.15, x1 = r.x1 + 0.15, z0 = r.z0 - 0.15, z1 = r.z1 + 0.15;
    pos.push(x0, Y, z0, x1, Y, z0, x1, Y, z1, x0, Y, z1);
    uvs.push(0, 0, 1, 0, 1, 1, 0, 1);
    idx.push(base, base + 2, base + 1, base, base + 3, base + 2);
    base += 4;
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(new Float32Array(pos.length).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  g.setIndex(idx);
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -3, polygonOffsetUnits: -3 });
  const wp = positionWorld.xz;
  // 사각형 가장자리로 갈수록 옅게(판이 바닥에 스며든 것처럼)
  const e = uv();
  const edge = smoothstep(0.0, 0.16, e.x).mul(smoothstep(0.0, 0.16, float(1).sub(e.x))).mul(smoothstep(0.0, 0.16, e.y)).mul(smoothstep(0.0, 0.16, float(1).sub(e.y)));
  if (theme === 'frost') {
    // 얼음 균열: 잡음의 0 등고선(abs 작은 곳)을 청백 빛줄기로, 그 밖은 반투명 얼음 판
    const n = mx_noise_float(vec3(wp.mul(0.9), time.mul(0.05)));
    const crack = float(1).sub(smoothstep(0.02, 0.09, abs(n)));
    const sheen = mx_noise_float(vec3(wp.mul(0.25), 3.3)).mul(0.5).add(0.5);
    // 눈 덮인 바닥(서리 묘지)과 구분되게 바탕은 짙은 청록 얼음, 균열은 밝은 청백 빛(c016 화면 확인: 흰 바탕에 흰 균열은 안 보였다)
    m.colorNode = mix(mix(vec3(0.04, 0.14, 0.3), vec3(0.16, 0.4, 0.62), sheen), vec3(0.75, 1.7, 2.4), crack);
    m.opacityNode = edge.mul(mix(float(0.8), float(1.0), crack));
  } else if (theme === 'sand') {
    // 유사(C2 R5): 사암 바닥보다 짙은 황토 모래가 가운데로 천천히 빨려 드는 소용돌이 무늬 + 가장자리 금빛 부스러기.
    // 판정 칸 가운데를 향해 흐르는 줄무늬는 각도 대신 잡음 좌표를 시간으로 밀어 만든다(점멸 없음)
    // C2 env 캡처: 밝은 크림 바탕은 사암 바닥과 같은 밝기라 위험 칸이 묻혔다 → 바탕을 짙은 황토·갈색으로 내리고,
    // 가는 금빛 흐름선(잡음 0 등고선)과 짙은 가장자리 테로 "빨려 드는 구덩이"가 한눈에 읽히게 한다
    const swirl = mx_noise_float(vec3(wp.mul(0.7).add(time.mul(0.18)), time.mul(0.06))).mul(0.5).add(0.5);
    const flow = float(1).sub(smoothstep(0.02, 0.07, abs(mx_noise_float(vec3(wp.mul(1.3).sub(time.mul(0.22)), 1.7)))));
    const body = mix(vec3(0.13, 0.07, 0.02), vec3(0.34, 0.2, 0.07), swirl);
    const rim = smoothstep(0.0, 0.1, e.x).mul(smoothstep(0.0, 0.1, float(1).sub(e.x))).mul(smoothstep(0.0, 0.1, e.y)).mul(smoothstep(0.0, 0.1, float(1).sub(e.y)));
    m.colorNode = mix(body.mul(mix(float(0.55), float(1), rim)), vec3(1.3, 0.95, 0.4), flow.mul(0.55));
    m.opacityNode = edge.mul(0.92);
  } else if (theme === 'void') {
    // 심연 균열(C2 R6): 흑요석 바닥에 뚫린 허공 — 거의 검은 남보라 바탕 + 균열선을 따라 흐르는 자홍 빛 + 깊은 곳의 별빛 점
    const n = mx_noise_float(vec3(wp.mul(0.8), time.mul(0.06)));
    const crack = float(1).sub(smoothstep(0.03, 0.12, abs(n)));
    const stars = smoothstep(0.82, 0.9, mx_noise_float(vec3(wp.mul(5.0), 9.1)).mul(0.5).add(0.5));
    const body = mix(vec3(0.02, 0.0, 0.05), vec3(0.12, 0.03, 0.24), mx_noise_float(vec3(wp.mul(0.3), time.mul(0.04))).mul(0.5).add(0.5));
    m.colorNode = body.add(vec3(1.3, 0.35, 2.2).mul(crack)).add(vec3(0.7, 0.6, 1.2).mul(stars.mul(0.6)));
    m.opacityNode = edge.mul(mix(float(0.92), float(1.0), crack));
  } else {
    // 독 늪: 짙은 녹갈색 바탕 + 천천히 흐르는 연두 기포 얼룩
    const flow = mx_noise_float(vec3(wp.mul(0.55).add(time.mul(0.12)), time.mul(0.08)));
    const bubble = smoothstep(0.35, 0.62, mx_noise_float(vec3(wp.mul(1.6), time.mul(0.35))).mul(0.5).add(0.5));
    // 이끼 바닥(늪 사원)과 구분되게 바탕은 탁한 독녹색, 기포는 형광 연두(c016 화면 확인 뒤 대비를 키움)
    const body = mix(vec3(0.1, 0.24, 0.02), vec3(0.34, 0.66, 0.05), flow.mul(0.5).add(0.5));
    m.colorNode = mix(body, vec3(0.9, 1.7, 0.25), bubble.mul(0.65));
    m.opacityNode = edge.mul(0.94);
  }
  const mesh = new THREE.Mesh(g, m);
  mesh.name = 'hazard-pools-' + theme;
  mesh.renderOrder = 2;
  return mesh;
}
